import React from 'react'
import moment from 'moment'
import type { ProductSwitcherApp } from 'prosci-ui'
import { useAuth } from '../contexts/AuthContext'
import { useProductDisplayConfigQuery } from './queries/useProductDisplayConfigQuery'
import { isProductExpired } from '../features/dashboard/utils/expiringProducts'
import { Award, BookOpen, LegacyResources, Laptop, PencilRuler } from '../utils/icons'
import {
  type DrawerProduct,
  type EnrichedDrawerProduct,
  mergeDrawerProductsWithDisplayConfig,
} from '../utils/productDisplayUtils'

type IconComponent = React.ComponentType<{ className?: string }>

/**
 * Wraps a CMS icon URL as a component so it fits `ProductSwitcherApp`'s icon-component
 * prop. A URL can fail to resolve — an unpublished asset, a retired upload — which would
 * leave a broken-image glyph, so a failed load falls back to the product's own icon.
 */
// A fresh component type per call would remount every icon (and re-request its image) on
// each memo pass, so wrappers are reused per fallback + URL.
const urlIconCache = new Map<IconComponent, Map<string, IconComponent>>()

export function makeUrlIcon(url: string | undefined, Fallback: IconComponent): IconComponent {
  if (!url) return Fallback

  let byUrl = urlIconCache.get(Fallback)
  if (!byUrl) {
    byUrl = new Map()
    urlIconCache.set(Fallback, byUrl)
  }
  const cached = byUrl.get(url)
  if (cached) return cached

  function ProductIcon({ className }: { className?: string }) {
    const [imageFailed, setImageFailed] = React.useState(false)
    if (imageFailed) return <Fallback className={className} />
    return <img src={url} alt="" className={className} onError={() => setImageFailed(true)} />
  }
  byUrl.set(url, ProductIcon)
  return ProductIcon
}

type IconHeuristicInput = Partial<
  Pick<
    DrawerProduct,
    'code' | 'productTag' | 'isKnowledgeHub' | 'isResearchHub' | 'isInstructorHub' | 'isPrework'
  >
>

const LEGACY_RESOURCES_GROUP_ID = 'legacy-resources'

export function heuristicIcon(product: IconHeuristicInput): IconComponent {
  if (product.code === LEGACY_RESOURCES_GROUP_ID) return LegacyResources
  if (product.isKnowledgeHub || product.isResearchHub || product.isInstructorHub) return BookOpen
  if (product.isPrework || product.productTag === 'eLearning') return Laptop
  if (product.code?.toLowerCase().includes('pcacp')) return Award
  return PencilRuler
}

/**
 * Warns 30 days out, but only for access that won't renew itself — a lapsing
 * subscription re-bills silently and shouldn't nag.
 */
function expiresLabel(product: EnrichedDrawerProduct): string | undefined {
  if (!product.expirationDate || product.hasRenewableSubscription) return undefined
  const expiresAt = moment(product.expirationDate)
  if (expiresAt <= moment() || expiresAt > moment().add(30, 'days')) return undefined
  return expiresAt.format('MMM. Do, YYYY')
}

function expiredLabel(product: EnrichedDrawerProduct): string | undefined {
  if (!isProductExpired(product)) return undefined
  return moment(product.expirationDate).format('MMM. Do, YYYY')
}

/**
 * Maps the user's entitled products onto the nav app-switcher's tiles, applying the
 * Contentful `portalDrawer` display config (icon, display name, ordering) and its
 * grouped drill-downs. `onLaunch` receives the selected product's id — grouped tiles
 * report the member the user drilled into, never the synthetic group itself.
 */
export function useProductSwitcherApps(
  products: DrawerProduct[] | undefined,
  onLaunch: (productId: string) => void
): ProductSwitcherApp[] {
  const { user } = useAuth()
  const locale = user?.preferredLocale || 'en-US'
  const { data: displayConfig } = useProductDisplayConfigQuery(locale)

  return React.useMemo(() => {
    const enriched = mergeDrawerProductsWithDisplayConfig(products ?? [], displayConfig, locale)

    return enriched.map((product) => ({
      id: String(product.id),
      name: product.displayName,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- icon wrappers vs. ProductSwitcherApp's icon type disagree across the prosci-ui/app @types/react boundary
      icon: makeUrlIcon(product.iconUrl, heuristicIcon(product)) as any,
      expiresLabel: expiresLabel(product),
      expiredLabel: expiredLabel(product),
      itemsSubtitle: product.itemsSubtitle,
      items: product.items?.map((item) => ({
        id: String(item.id),
        name: item.name,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above
        icon: makeUrlIcon(item.iconUrl, heuristicIcon(item)) as any,
        onSelect: () => onLaunch(String(item.id)),
      })),
      onSelect: () => onLaunch(String(product.id)),
    }))
  }, [products, displayConfig, locale, onLaunch])
}

export default useProductSwitcherApps
